import { useState } from "react";
import {
  DragEndEvent,
  DragStartEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";

// MouseSensor + TouchSensor instead of PointerSensor: some input stacks emit only
// legacy mouse events, and a PointerSensor-only setup silently disables dragging there.
export function useDndSensors() {
  return useSensors(
    useSensor(MouseSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
}

// For Sorting Menu
export function useReorderList<T>(params: {
  items: T[];
  setItems: (next: T[] | ((prev: T[]) => T[])) => void;
  getItemId: (item: T) => string;
  onDragStart?: () => void;
  onDragEnd?: () => void;
}) {
  const { items, setItems, getItemId, onDragStart, onDragEnd } = params;

  const [activeId, setActiveId] = useState<string | null>(null);
  const sensors = useDndSensors();

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(String(event.active.id));
    onDragStart?.();
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    setActiveId(null);
    onDragEnd?.();

    if (!over) return;

    const activeKey = String(active.id);
    const overKey = String(over.id);
    if (activeKey === overKey) return;

    setItems((prev) => {
      const oldIndex = prev.findIndex((it) => getItemId(it) === activeKey);
      const newIndex = prev.findIndex((it) => getItemId(it) === overKey);
      if (oldIndex === -1 || newIndex === -1) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  return {
    sensors,
    activeId,
    handleDragStart,
    handleDragEnd,
    collisionDetection: closestCenter,
    getItemIds: () => items.map(getItemId),
  };
}

// For Columns Menu
export function useReorderSubsetInPlace<T>(params: {
  subsetItems: T[];
  setAllItems: (next: T[] | ((prev: T[]) => T[])) => void;
  getItemId: (item: T) => string;
  onDragStart?: () => void;
  onDragEnd?: () => void;
}) {
  const { subsetItems, setAllItems, getItemId, onDragStart, onDragEnd } =
    params;

  const [activeId, setActiveId] = useState<string | null>(null);
  const sensors = useDndSensors();

  const movableIds = subsetItems.map(getItemId);
  const movableSet = new Set(movableIds);

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(String(event.active.id));
    onDragStart?.();
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    setActiveId(null);
    onDragEnd?.();

    if (!over) return;

    const activeKey = String(active.id);
    const overKey = String(over.id);
    if (activeKey === overKey) return;

    setAllItems((prevAll) => {
      if (!Array.isArray(prevAll) || prevAll.length === 0) return prevAll;

      const currentSubset = prevAll.filter((it) =>
        movableSet.has(getItemId(it)),
      );

      const oldIndex = currentSubset.findIndex(
        (it) => getItemId(it) === activeKey,
      );
      const newIndex = currentSubset.findIndex(
        (it) => getItemId(it) === overKey,
      );
      if (oldIndex === -1 || newIndex === -1) return prevAll;

      const reorderedSubset = arrayMove(currentSubset, oldIndex, newIndex);

      const slots: number[] = [];
      for (let i = 0; i < prevAll.length; i++) {
        if (movableSet.has(getItemId(prevAll[i]))) slots.push(i);
      }

      const nextAll = [...prevAll];
      for (let i = 0; i < slots.length; i++) {
        nextAll[slots[i]] = reorderedSubset[i];
      }

      return nextAll;
    });
  };

  return {
    sensors,
    activeId,
    handleDragStart,
    handleDragEnd,
    collisionDetection: closestCenter,
    getItemIds: () => subsetItems.map(getItemId),
  };
}
